import Button from "../../../../shared/components/Button";
import IconButtonContent from "../../../../shared/components/IconButtonContent";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { Club } from "../../shared/types/club";
import { buildClubAddressQuery } from "../../shared/utils/clubMapPosition";

function buildDirectionsUrl(club: Club) {
  const address = buildClubAddressQuery(club);
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  if (club.latitude !== null && club.longitude !== null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${club.latitude},${club.longitude}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

type Props = {
  club: Club;
  compact?: boolean;
  includeDetail?: boolean;
};

type ClubActionItem = {
  key:
    | "detail"
    | "booking"
    | "directions"
    | "website"
    | "instagram"
    | "lances";
  label: string;
  icon: string;
  href: string;
  target?: string;
  rel?: string;
  priority: number;
};

export function buildClubActionItems(
  club: Club,
  includeDetail = false,
): ClubActionItem[] {
  const t = siteTexts.clubs.actions;
  const lancesLabel = club.lancesPlatformName || t.lances;

  return [
    includeDetail
      ? {
          key: "detail",
          label: t.viewClub,
          icon: "open_in_new",
          href: `#/clubes/${club.slug}`,
          priority: 0,
        }
      : null,
    club.bookingUrl
      ? {
          key: "booking",
          label: t.booking,
          icon: "calendar_month",
          href: club.bookingUrl,
          target: "_blank",
          rel: "noreferrer",
          priority: 1,
        }
      : null,
    {
      key: "directions",
      label: t.directions,
      icon: "near_me",
      href: buildDirectionsUrl(club),
      target: "_blank",
      rel: "noreferrer",
      priority: 2,
    },
    club.websiteUrl
      ? {
          key: "website",
          label: t.site,
          icon: "public",
          href: club.websiteUrl,
          target: "_blank",
          rel: "noreferrer",
          priority: 3,
        }
      : null,
    club.instagramUrl
      ? {
          key: "instagram",
          label: t.instagram,
          icon: "photo_camera",
          href: club.instagramUrl,
          target: "_blank",
          rel: "noreferrer",
          priority: 4,
        }
      : null,
    club.lancesUrl
      ? {
          key: "lances",
          label: lancesLabel,
          icon: "open_in_new",
          href: club.lancesUrl,
          target: "_blank",
          rel: "noreferrer",
          priority: 5,
        }
      : null,
  ].filter(Boolean) as ClubActionItem[];
}

export default function ClubActionButtons({
  club,
  compact = false,
  includeDetail = false,
}: Props) {
  const className = `clubs-actions${compact ? " clubs-actions--compact" : ""}`;
  const actions = buildClubActionItems(club, includeDetail);
  const primaryActionKey = actions.reduce<string | null>((selected, action) => {
    if (!selected) return action.key;
    const current = actions.find((item) => item.key === selected);
    if (!current) return action.key;
    return action.priority < current.priority ? action.key : selected;
  }, null);

  return (
    <div className={className}>
      {actions.map((action) => (
        <Button
          key={action.key}
          type="button"
          variant={action.key === primaryActionKey ? "primary" : "pill"}
          href={action.href}
          target={action.target}
          rel={action.rel}
        >
          <IconButtonContent icon={action.icon}>{action.label}</IconButtonContent>
        </Button>
      ))}
    </div>
  );
}
